import { useEffect, useState, type FormEvent } from 'react';
import { isApiError } from '../../lib/api';
import { listAccounts, type AccountRow } from '../../lib/accounts-api';
import {
  createApiKey,
  listApiKeys,
  revokeApiKey,
  updateApiKey,
  type ApiKeyRow,
  type ApiKeysState,
} from '../../lib/api-keys-api';
import { serverOrigin } from '../../lib/server-origin';
import { useAgentStore } from '../../stores/agent';
import styles from './admin.module.css';
import { confirmDialog } from '../../stores/confirm';

// Admin → API keys. Keys let an external system call the public API
// (/api/v1, docs/api.md) on behalf of the account, limited to the scopes and
// sites chosen here. The key is shown once at creation; only a hash is kept.

type Expiry = '' | '30' | '90' | '365';

interface Draft {
  id?: string;
  name: string;
  scopes: string[];
  allSites: boolean;
  siteIds: string[];
  expiry: Expiry;
}

const blank = (): Draft => ({ name: '', scopes: [], allSites: true, siteIds: [], expiry: '' });

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');

export function AdminApiKeys() {
  const role = useAgentStore((s) => s.current?.role);
  const isPlatformAdmin = role === 'super-admin';
  const allowed = role === 'super-admin' || role === 'account-admin';
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [accountId, setAccountId] = useState<string | undefined>(undefined);
  const [state, setState] = useState<ApiKeysState | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [secret, setSecret] = useState<{ name: string; value: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function load() {
    try {
      setState(await listApiKeys(accountId));
      setError(null);
    } catch {
      setError('Could not load the API keys.');
    }
  }
  useEffect(() => {
    if (allowed) void load();
  }, [accountId, allowed]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isPlatformAdmin) listAccounts().then(setAccounts).catch(() => {});
  }, [isPlatformAdmin]);

  if (!allowed) {
    return (
      <section className={styles.section}>
        <p className={styles.kicker}>API keys</p>
        <p className={styles.error}>Only account admins can manage API keys.</p>
      </section>
    );
  }

  const toggle = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!draft) return;
    if (draft.scopes.length === 0) return setError('Pick at least one scope.');
    if (!draft.allSites && draft.siteIds.length === 0) return setError('Pick at least one site, or allow all sites.');
    setBusy(true); setError(null); setNotice(null);
    const input = { name: draft.name.trim(), scopes: draft.scopes, siteIds: draft.allSites ? null : draft.siteIds };
    try {
      if (draft.id) {
        await updateApiKey(draft.id, input);
        setNotice(`Saved “${input.name}”. The change applies to the next request.`);
      } else {
        const r = await createApiKey(
          { ...input, expiresInDays: draft.expiry ? (Number(draft.expiry) as 30 | 90 | 365) : null },
          accountId,
        );
        setSecret({ name: r.key.name, value: r.secret });
      }
      setDraft(null);
      await load();
    } catch (err) {
      setError(isApiError(err) && err.status === 400 ? 'Check the name, scopes and sites.' : 'Could not save the key.');
    } finally {
      setBusy(false);
    }
  }

  async function revoke(k: ApiKeyRow) {
    if (!(await confirmDialog({ title: `Revoke “${k.name}”?`, message: 'Anything using it stops working immediately. This cannot be undone.', confirmLabel: 'Revoke key' }))) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      await revokeApiKey(k.id);
      setNotice(`Revoked “${k.name}”.`);
      await load();
    } catch {
      setError('Could not revoke the key.');
    } finally {
      setBusy(false);
    }
  }

  const siteName = (id: string) => state?.sites.find((s) => s.id === id)?.name ?? 'removed site';
  const example = secret ? `curl ${serverOrigin()}/api/v1/me \\\n  -H "Authorization: Bearer ${secret.value}"` : '';

  return (
    <section className={styles.section}>
      <p className={styles.kicker}>API keys</p>
      <h2 className={styles.title}>Keys for your integrations.</h2>
      <p style={{ color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-sm)', margin: 0 }}>
        An API key lets another system use the Openline API on behalf of this account, for example to read
        conversations or send a reply from your own app. Each key gets only the scopes and sites you pick, can
        be revoked at any time, and is shown once when it is created.
      </p>

      {isPlatformAdmin && accounts.length > 1 && (
        <label className={styles.field} style={{ maxWidth: 360, marginTop: 'var(--inic-space-3)' }}>
          <span className={styles.label}>Account</span>
          <select className={styles.select} value={accountId ?? ''} onChange={(e) => setAccountId(e.target.value || undefined)}>
            <option value="">My account</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </label>
      )}

      {error && <p className={styles.error} role="alert">{error}</p>}
      {notice && <p className={styles.notice} role="status">{notice}</p>}

      {secret && (
        <div className={styles.notice} role="status" style={{ display: 'grid', gap: 'var(--inic-space-2)', marginTop: 'var(--inic-space-3)' }}>
          <strong>Copy the key for “{secret.name}” now. It won’t be shown again.</strong>
          <div style={{ display: 'flex', gap: 'var(--inic-space-2)', alignItems: 'center' }}>
            <code className={styles.mono} style={{ flex: 1, wordBreak: 'break-all' }} aria-label="New API key">{secret.value}</code>
            <button type="button" className={styles.buttonGhost}
              onClick={() => void navigator.clipboard?.writeText(secret.value).then(() => setNotice('Key copied.'))}>Copy</button>
          </div>
          <span style={{ fontSize: 'var(--inic-size-xs)' }}>Try it:</span>
          <pre className={styles.embedBlock} style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{example}</pre>
          <div>
            <button type="button" className={styles.buttonGhost} onClick={() => setSecret(null)}>I’ve stored it</button>
          </div>
        </div>
      )}

      {state && state.keys.length === 0 && !draft && (
        <p style={{ color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-sm)' }}>No API keys yet.</p>
      )}

      {state && state.keys.length > 0 && (
        <div className={styles.tableWrap} style={{ marginTop: 'var(--inic-space-3)' }}>
          <table className={styles.table} aria-label="API keys">
            <thead>
              <tr><th>Name</th><th>Key</th><th>Scopes</th><th>Sites</th><th>Last used</th><th>Requests</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {state.keys.map((k) => (
                <tr key={k.id} style={k.status === 'active' ? undefined : { opacity: 0.6 }}>
                  <td>
                    {k.name}
                    <span style={{ display: 'block', fontSize: 'var(--inic-size-xs)', color: 'var(--inic-ink-muted)' }}>
                      by {k.createdByName ?? '—'} · {new Date(k.createdAt).toLocaleDateString()}
                    </span>
                  </td>
                  <td className={styles.mono}>{k.prefix}…</td>
                  <td>
                    <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {k.scopes.map((s) => <span key={s} className={styles.badgeMuted}>{s}</span>)}
                    </span>
                  </td>
                  <td>{k.siteIds === null ? 'All sites' : k.siteIds.map(siteName).join(', ')}</td>
                  <td>
                    {fmt(k.lastUsedAt)}
                    {k.lastUsedIp && <span className={styles.mono} style={{ display: 'block', fontSize: 'var(--inic-size-xs)' }}>{k.lastUsedIp}</span>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {k.usage ? <>{k.usage.last24h} today<span style={{ display: 'block', fontSize: 'var(--inic-size-xs)', color: 'var(--inic-ink-muted)' }}>{k.usage.last30d} in 30 days{k.usage.errors30d ? ` · ${k.usage.errors30d} error${k.usage.errors30d === 1 ? '' : 's'}` : ''}</span></> : '—'}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <span className={k.status === 'active' ? styles.badgeAccent : styles.badgeMuted}>{k.status}</span>
                    {k.status === 'active' && k.expiresAt && (
                      <span style={{ display: 'block', fontSize: 'var(--inic-size-xs)', color: 'var(--inic-ink-muted)' }}>expires {new Date(k.expiresAt).toLocaleDateString()}</span>
                    )}
                  </td>
                  <td>
                    {k.status !== 'revoked' && (
                      <div style={{ display: 'flex', gap: 'var(--inic-space-2)', justifyContent: 'flex-end' }}>
                        {k.status === 'active' && (
                          <button type="button" className={styles.buttonGhost} disabled={busy}
                            onClick={() => { setSecret(null); setDraft({ id: k.id, name: k.name, scopes: k.scopes, allSites: k.siteIds === null, siteIds: k.siteIds ?? [], expiry: '' }); }}>
                            Edit
                          </button>
                        )}
                        <button type="button" className={styles.buttonDanger} disabled={busy} onClick={() => void revoke(k)}>Revoke</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!draft && (
        <div className={styles.actions} style={{ marginTop: 'var(--inic-space-3)' }}>
          <button type="button" className={styles.button} onClick={() => { setSecret(null); setNotice(null); setDraft(blank()); }}>
            Create API key →
          </button>
        </div>
      )}

      {draft && state && (
        <form className={styles.form} onSubmit={(e) => void submit(e)} style={{ marginTop: 'var(--inic-space-4)' }}>
          <label className={styles.field}>
            <span className={styles.label}>Name</span>
            <input className={styles.input} required maxLength={80} value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="CRM sync" />
          </label>
          {!draft.id && (
            <label className={styles.field}>
              <span className={styles.label}>Expires</span>
              <select className={styles.select} value={draft.expiry} onChange={(e) => setDraft({ ...draft, expiry: e.target.value as Expiry })}>
                <option value="">Never</option>
                <option value="30">In 30 days</option>
                <option value="90">In 90 days</option>
                <option value="365">In 1 year</option>
              </select>
            </label>
          )}

          <fieldset className={styles.field} style={{ gridColumn: '1 / -1', border: 0, padding: 0, margin: 0 }}>
            <legend className={styles.label}>Scopes — what the key may do</legend>
            {state.scopes.map((s) => (
              <label key={s.id} style={{ display: 'flex', gap: 'var(--inic-space-2)', alignItems: 'flex-start', marginTop: 'var(--inic-space-2)' }}>
                <input type="checkbox" checked={draft.scopes.includes(s.id)} style={{ marginTop: 3 }}
                  onChange={() => setDraft({ ...draft, scopes: toggle(draft.scopes, s.id) })} />
                <span>
                  <code className={styles.mono}>{s.id}</code>
                  <span style={{ display: 'block', color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-xs)' }}>{s.description}</span>
                </span>
              </label>
            ))}
          </fieldset>

          <fieldset className={styles.field} style={{ gridColumn: '1 / -1', border: 0, padding: 0, margin: 0 }}>
            <legend className={styles.label}>Sites</legend>
            <label style={{ display: 'flex', gap: 'var(--inic-space-2)', marginTop: 'var(--inic-space-2)' }}>
              <input type="radio" name="sites" checked={draft.allSites} onChange={() => setDraft({ ...draft, allSites: true })} />
              <span>All sites of the account, including sites added later</span>
            </label>
            <label style={{ display: 'flex', gap: 'var(--inic-space-2)', marginTop: 'var(--inic-space-1)' }}>
              <input type="radio" name="sites" checked={!draft.allSites} onChange={() => setDraft({ ...draft, allSites: false })} />
              <span>Only these sites:</span>
            </label>
            {!draft.allSites && (
              <div style={{ display: 'grid', gap: 'var(--inic-space-1)', paddingLeft: 'var(--inic-space-5)', marginTop: 'var(--inic-space-1)' }}>
                {state.sites.map((s) => (
                  <label key={s.id} style={{ display: 'flex', gap: 'var(--inic-space-2)' }}>
                    <input type="checkbox" checked={draft.siteIds.includes(s.id)}
                      onChange={() => setDraft({ ...draft, siteIds: toggle(draft.siteIds, s.id) })} />
                    <span>{s.name}{s.archivedAt ? ' (archived)' : ''}</span>
                  </label>
                ))}
              </div>
            )}
          </fieldset>

          <div className={styles.formActions}>
            <button type="submit" className={styles.button} disabled={busy}>{draft.id ? 'Save changes →' : 'Create key →'}</button>
            <button type="button" className={styles.buttonGhost} disabled={busy} onClick={() => { setDraft(null); setError(null); }}>Cancel</button>
          </div>
        </form>
      )}

      <p style={{ color: 'var(--inic-ink-muted)', fontSize: 'var(--inic-size-xs)', marginTop: 'var(--inic-space-4)' }}>
        Send the key as <code>Authorization: Bearer olk_…</code>. Each key may make 600 requests a minute.
        The endpoints and scopes are described in docs/api.md.
      </p>
    </section>
  );
}
